import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Printer } from 'lucide-react';
import { format } from 'date-fns';

interface Unit {
  id: string;
  unit_identifier: string;
  collection_date: string | null;
  volume_ml: number | null;
  total_nucleated_cells: number | null;
  cd34_positive_cells: number | null;
  viability_percentage: number | null;
  sterility?: string | null;
  unit_requested_date: string | null;
  unit_status: string;
  selected_for_infusion: boolean;
}

interface CellularProductInventoryReportProps {
  isOpen: boolean;
  onClose: () => void;
  units: Unit[];
  orderId: string;
  patientInfo?: {
    first_name?: string;
    last_name?: string;
    gender?: string;
    date_of_birth?: string;
    mrn?: string;
    abo_rh?: string;
    physician?: string;
  } | null;
}

export function CellularProductInventoryReport({ 
  isOpen, 
  onClose, 
  units, 
  orderId,
  patientInfo
}: CellularProductInventoryReportProps) {
  
  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd-MMM-yy');
    } catch (error) {
      return '';
    }
  };

  const formatDateFull = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch (error) {
      return '';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[99vw] w-[99vw] max-h-[90vh] overflow-x-hidden overflow-y-auto print:max-w-none print:max-h-none print:overflow-visible print:p-0">
        <DialogHeader className="print:hidden sticky top-0 bg-white z-50 border-b border-gray-200 pb-4 px-6 pt-6">
          <DialogTitle className="flex items-center justify-between w-full">
            <span className="text-lg font-semibold">Cellular Product Inventory Report</span>
            <div className="flex space-x-2 ml-4">
              <Button onClick={handlePrint} variant="outline" size="sm" className="whitespace-nowrap">
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button onClick={onClose} variant="ghost" size="sm" className="whitespace-nowrap">
                Close
              </Button>
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Cellular product inventory report showing unit details and return request information
          </DialogDescription>
        </DialogHeader>
        
        <div className="bg-white text-black print:bg-white print:text-black text-sm leading-tight p-8 print:landscape print:p-4 print:text-[10px]" style={{ fontFamily: 'Arial, sans-serif' }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-16 h-16 mr-4">
                <div className="w-full h-full bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  P
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">Progenitor</div>
                <div className="text-sm text-gray-600">Cell Therapy</div>
              </div>
            </div>
            <div className="text-2xl font-bold text-center flex-1">
              Cellular Product Inventory Report and Return Request Form
            </div>
          </div>

          {/* Company and Clinical Site Information */}
          <div className="flex justify-between mb-6 text-xs">
            <div className="w-1/2 space-y-1">
              <div className="font-bold">Progenitor Cell Therapy</div>
              <div>Cellular Therapy Laboratory</div>
              <div>4 Pearl Court, Suite B</div>
              <div>Allendale, NJ 07401</div>
              <div>Phone: 201-543-4585</div>
              <div>Fax: 201-783-8633</div>
            </div>
            
            <div className="w-1/2 text-right space-y-1">
              <div className="font-bold">Clinical Site</div>
              <div>Hackensack University MC - NJ01</div>
              <div>30 Prospect Av.</div>
              <div>Hackensack NJ 07601</div>
              <div>551-996-2000</div>
            </div>
          </div>

          {/* Patient Information */}
          <div className="mb-6 text-xs">
            <div className="w-1/2 pr-4">
              <div className="font-bold text-red-600 mb-2 border-b border-gray-300 pb-1">
                Patient (Recipient) Information
              </div>
              <div className="space-y-1">
                <div>
                  <span className="text-red-600 font-semibold">
                    {patientInfo?.last_name || ''}, {patientInfo?.first_name || ''}
                  </span>
                  <span className="ml-4 text-red-600 font-semibold">
                    {patientInfo?.gender === 'male' ? 'Male' : patientInfo?.gender === 'female' ? 'Female' : ''}
                  </span>
                  <span className="ml-4 text-red-600 font-semibold">
                    DOB: {formatDateFull(patientInfo?.date_of_birth || null)}
                  </span>
                </div>
                <div className="text-red-600 font-semibold">
                  MRN: {patientInfo?.mrn || ''}
                  <span className="ml-4">ABO/Rh: {patientInfo?.abo_rh || ''}</span>
                </div>
                <div className="text-red-600 font-semibold">
                  Physician: {patientInfo?.physician || ''}
                </div>
              </div>
            </div>
          </div>

          {/* Units Table */}
          <div className="mb-6">
            <table className="w-full border-collapse border border-gray-400 text-xs print:text-[11px] print:w-full" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-400 p-1 text-left font-medium text-xs leading-tight" style={{ width: '12%' }}>PCT Unit Number</th>
                  <th className="border border-gray-400 p-1 text-left font-medium text-xs leading-tight" style={{ width: '8%' }}>Collection<br/>Date</th>
                  <th className="border border-gray-400 p-1 text-left font-medium text-xs leading-tight" style={{ width: '6%' }}>Vol<br/>(ml)</th>
                  <th className="border border-gray-400 p-1 text-left font-medium text-xs leading-tight" style={{ width: '8%' }}>TNC<br/>(x10⁶/kg)</th>
                  <th className="border border-gray-400 p-1 text-left font-medium text-xs leading-tight" style={{ width: '9%' }}>Total CD34<br/>(x10⁶/kg)</th>
                  <th className="border border-gray-400 p-1 text-left font-medium text-xs leading-tight" style={{ width: '8%' }}>CD3<br/>(x10⁶/kg)</th>
                  <th className="border border-gray-400 p-1 text-left font-medium text-xs leading-tight" style={{ width: '6%' }}>Via<br/>(%)</th>
                  <th className="border border-gray-400 p-1 text-left font-medium text-xs leading-tight" style={{ width: '8%' }}>Sterility</th>
                  <th className="border border-gray-400 p-1 text-left font-medium text-xs leading-tight" style={{ width: '8%' }}>Ordered<br/>Date</th>
                  <th className="border border-gray-400 p-1 text-left font-medium text-xs leading-tight" style={{ width: '9%' }}>Unit Requested<br/>For</th>
                  <th className="border border-gray-400 p-1 text-left font-medium text-xs leading-tight" style={{ width: '8%' }}>Infusion<br/>Order</th>
                </tr>
              </thead>
              <tbody>
                {units.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="border border-gray-400 p-4 text-center text-gray-500">
                      No units available for this order
                    </td>
                  </tr>
                ) : (
                  units.map((unit) => (
                    <tr key={unit.id} className="hover:bg-gray-50">
                      <td className="border border-gray-400 p-1 text-xs" style={{ width: '12%' }}>{unit.unit_identifier}</td>
                      <td className="border border-gray-400 p-1 text-xs" style={{ width: '8%' }}>{formatDate(unit.collection_date)}</td>
                      <td className="border border-gray-400 p-1 text-xs" style={{ width: '6%' }}>{unit.volume_ml || ''}</td>
                      <td className="border border-gray-400 p-1 text-xs" style={{ width: '8%' }}>
                        {unit.total_nucleated_cells ? (unit.total_nucleated_cells / 1000000).toFixed(1) : ''}
                      </td>
                      <td className="border border-gray-400 p-1 text-xs" style={{ width: '9%' }}>
                        {unit.cd34_positive_cells ? (unit.cd34_positive_cells / 1000000).toFixed(1) : ''}
                      </td>
                      <td className="border border-gray-400 p-1 text-xs" style={{ width: '8%' }}></td> {/* CD3 - not in our data */}
                      <td className="border border-gray-400 p-1 text-xs" style={{ width: '6%' }}>
                        {unit.viability_percentage ? `${unit.viability_percentage}` : ''}
                      </td>
                      <td className="border border-gray-400 p-1 text-xs" style={{ width: '8%' }}>{unit.sterility || 'No Growth'}</td>
                      <td className="border border-gray-400 p-1 text-xs" style={{ width: '8%' }}>
                        {unit.selected_for_infusion ? formatDate(new Date().toISOString()) : ''}
                      </td>
                      <td className="border border-gray-400 p-1 text-xs" style={{ width: '9%' }}>
                        {unit.unit_requested_date ? formatDate(unit.unit_requested_date) : 'Not set'}
                      </td>
                      <td className="border border-gray-400 p-1 text-center text-xs" style={{ width: '8%' }}>
                        {unit.selected_for_infusion ? (
                          <span className="capitalize font-medium text-[10px]">
                            {unit.unit_status === 'shipped' ? 'Shipped' : 
                             unit.unit_status === 'infused' ? 'Infused' : 
                             unit.unit_status === 'ordered' ? 'Ordered' : 'Available'}
                          </span>
                        ) : (
                          <div className="flex items-center justify-center">
                            <div className="w-2 h-2 border border-black"></div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
                
                {/* Add empty rows to match the format */}
                {Array.from({ length: Math.max(0, 6 - units.length) }).map((_, index) => (
                  <tr key={`empty-${index}`}>
                    <td className="border border-gray-400 p-1 h-8" style={{ width: '12%' }}></td>
                    <td className="border border-gray-400 p-1" style={{ width: '8%' }}></td>
                    <td className="border border-gray-400 p-1" style={{ width: '6%' }}></td>
                    <td className="border border-gray-400 p-1" style={{ width: '8%' }}></td>
                    <td className="border border-gray-400 p-1" style={{ width: '9%' }}></td>
                    <td className="border border-gray-400 p-1" style={{ width: '8%' }}></td>
                    <td className="border border-gray-400 p-1" style={{ width: '6%' }}></td>
                    <td className="border border-gray-400 p-1" style={{ width: '8%' }}></td>
                    <td className="border border-gray-400 p-1" style={{ width: '8%' }}></td>
                    <td className="border border-gray-400 p-1" style={{ width: '9%' }}></td>
                    <td className="border border-gray-400 p-1 text-center" style={{ width: '8%' }}>
                      <div className="flex items-center justify-center">
                        <div className="w-2 h-2 border border-black"></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer Information */}
          <div className="text-xs text-gray-600 mt-8">
            <div>Report generated on: {format(new Date(), 'dd-MMM-yyyy HH:mm')}</div>
            <div>Order ID: {orderId}</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}